import React from 'react';
import { useNavigate } from 'react-router-dom';
import './DisclaimerPage.css';

const DisclaimerPage = () => {
  const navigate = useNavigate();

  return (
    <div className="disclaimer-page">
      <div className="cosmic-bg"></div>
      
      <div className="disclaimer-container">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="disclaimer-card">
          <div className="disclaimer-header">
            <h1 className="disclaimer-title">⚠️ Important Disclaimer</h1>
            <p className="disclaimer-subtitle">AI-Powered Predictions & Usage Guidelines</p>
          </div>

          <div className="disclaimer-content">
            {/* AI Usage Warning */}
            <section className="disclaimer-section warning">
              <h2 className="section-title">
                <span className="section-icon">🤖</span>
                Artificial Intelligence Usage
              </h2>
              <div className="section-content">
                <p>
                  This application uses <strong>Artificial Intelligence (AI)</strong> and machine learning algorithms 
                  to generate predictions based on historical life event patterns. The predictions are generated by 
                  analyzing statistical patterns and correlations in data from the general public across the world
                  and is not specific to one individual or group. The probability percentage you see means that among the sample
                   of thousands of historical data, this particular event happened (in the past) in the mentioned percentage.
                   It does not mean that your future has the same probablity. It means people who took those sequence of events
                   encountered the event in the specified probability.
                </p>
                <ul className="disclaimer-list">
                  <li>AI predictions are <strong>probabilistic</strong>, not deterministic</li>
                  <li>Results are based on <strong>statistical patterns</strong>, not certainty</li>
                  <li>The AI model learns from <strong>historical data</strong> and may not account for unique circumstances</li>
                  <li>Predictions should be viewed as <strong>possibilities</strong>, not guarantees</li>
                </ul>
              </div>
            </section>

            {/* Entertainment Purpose */}
            <section className="disclaimer-section entertainment">
              <h2 className="section-title">
                <span className="section-icon">🎭</span>
                For insightfulness Purposes Only
              </h2>
              <div className="section-content">
                <p>
                  <strong>This service is provided for insight and informational purposes only. </strong>
                  The predictions generated by this application should NOT be used as:
                </p>
                <ul className="disclaimer-list">
                  <li>Professional advice (medical, legal, financial, or psychological)</li>
                  <li>A substitute for professional consultation</li>
                  <li>Definitive forecasts of future events</li>
                  <li>Basis for making important life decisions</li>
                  <li>Guaranteed outcomes or certainties</li>
                </ul>
              </div>
            </section>

            {/* No Liability */}
            <section className="disclaimer-section liability">
              <h2 className="section-title">
                <span className="section-icon">📜</span>
                Limitation of Liability
              </h2>
              <div className="section-content">
                <p>
                  By using this service, you acknowledge and agree that:
                </p>
                <ul className="disclaimer-list">
                  <li>
                    <strong>No Warranty:</strong> We make no warranties, express or implied, regarding the accuracy, 
                    completeness, or reliability of predictions
                  </li>
                  <li>
                    <strong>No Liability:</strong> We are not liable for any decisions made or actions taken based 
                    on predictions provided by this service
                  </li>
                  <li>
                    <strong>User Responsibility:</strong> You are solely responsible for your interpretation and use 
                    of any predictions
                  </li>
                  <li>
                    <strong>No Professional Relationship:</strong> Use of this service does not create any 
                    professional-client relationship
                  </li>
                </ul>
              </div>
            </section>

            {/* Data Privacy */}
            <section className="disclaimer-section privacy">
              <h2 className="section-title">
                <span className="section-icon">🔒</span>
                Data Privacy & Security
              </h2>
              <div className="section-content">
                <p>
                  Your privacy is important to us. Please note:
                </p>
                <ul className="disclaimer-list">
                  <li>Personal information is stored securely and used only for service provision</li>
                  <li>Life event data is used to improve prediction accuracy</li>
                  <li>We do not share your personal data with third parties without consent</li>
                  <li>You can request deletion of your data at any time</li>
                  <li>Data is stored in JSON format on secure servers</li>
                </ul>
              </div>
            </section>

            {/* Accuracy Notice */}
            <section className="disclaimer-section accuracy">
              <h2 className="section-title">
                <span className="section-icon">📊</span>
                Accuracy & Limitations
              </h2>
              <div className="section-content">
                <p>
                  AI predictions have inherent limitations:
                </p>
                <ul className="disclaimer-list">
                  <li>
                    <strong>Historical Bias:</strong> Predictions are based on past patterns which may not reflect 
                    future realities
                  </li>
                  <li>
                    <strong>Individual Variation:</strong> Every person's life path is unique and may deviate from 
                    statistical norms
                  </li>
                  <li>
                    <strong>External Factors:</strong> Unforeseen events, personal choices, and external circumstances 
                    can significantly alter outcomes
                  </li>
                  <li>
                    <strong>Model Limitations:</strong> AI models are limited by training data quality and quantity
                  </li>
                </ul>
              </div>
            </section>

            {/* Recommendations */}
            <section className="disclaimer-section recommendations">
              <h2 className="section-title">
                <span className="section-icon">💡</span>
                Recommendations
              </h2>
              <div className="section-content">
                <p>
                  For best results and responsible use:
                </p>
                <ul className="disclaimer-list">
                  <li>Treat predictions as <strong>one perspective</strong> among many</li>
                  <li>Always consult qualified professionals for important decisions</li>
                  <li>Use predictions as a tool for reflection, not as directives</li>
                  <li>Maintain realistic expectations about prediction accuracy</li>
                  <li>Focus on the journey, not just predicted outcomes</li>
                  <li>Remember that <strong>you control your future</strong> through your choices and actions</li>
                </ul>
              </div>
            </section>

            {/* Health Warnings */}
            <section className="disclaimer-section health-warning">
              <h2 className="section-title">
                <span className="section-icon">⚕️</span>
                Health & Medical Disclaimer
              </h2>
              <div className="section-content">
                <p className="warning-text">
                  <strong>IMPORTANT:</strong> Predictions related to health conditions are for informational purposes 
                  only and should NEVER be used for self-diagnosis or treatment decisions.
                </p>
                <ul className="disclaimer-list">
                  <li>Always consult qualified healthcare professionals for medical concerns</li>
                  <li>Do not delay seeking medical advice based on predictions</li>
                  <li>Health predictions are NOT medical diagnoses</li>
                  <li>The service does not replace professional medical care</li>
                </ul>
              </div>
            </section>

            {/* Age Restriction */}
            <section className="disclaimer-section age">
              <h2 className="section-title">
                <span className="section-icon">🔞</span>
                Age Restriction
              </h2>
              <div className="section-content">
                <p>
                  This service is intended for users aged 18 and above. By using this service, you confirm that 
                  you are at least 18 years old or have parental/guardian consent.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="disclaimer-section contact">
              <h2 className="section-title">
                <span className="section-icon">📧</span>
                Questions or Concerns?
              </h2>
              <div className="section-content">
                <p>
                  If you have any questions about this disclaimer or the service, please contact us at:
                </p>
                <p className="contact-email">support@futuresight.com</p>
              </div>
            </section>

            {/* Acceptance */}
            <div className="acceptance-notice">
              <p>
                By continuing to use this service, you acknowledge that you have read, understood, and agree 
                to this disclaimer and its terms.
              </p>
              <p className="last-updated">
                Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerPage;